import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LOADER_DURATION } from '../../helpers/Constants';
import { siteApi } from '../../services/site';
import { contentApi } from '../../services/content';
import _ from 'lodash';

const wait = () =>
    new Promise((resolve) => {
        setTimeout(() => resolve(), LOADER_DURATION);
    });

export const stopLoading = createAsyncThunk(
    'common/stopLoading',
    async () => {
        await wait();
        return true;
    }
)

/**
 * `categoryHierarchyData` is a critical piece of state for managing the site's category structure.
 * It is populated by the `getCategories` API endpoint and processed by the `buildCategoryHierarchy` helper.
 * 
 * STRUCTURE:
 * It is an object where each key is a `CategoryID` (string).
 * The value for each key is an object containing:
 *   - All original properties from the API for that category (e.g., `CategoryID`, `Name`, `ParentCategoryID`).
 *   - `allChildrenIds`: An array of strings, holding the `CategoryID` of every descendant (children, grandchildren, etc.).
 *   - `allParentIds`: An array of strings, holding the `CategoryID` of every ancestor (parent, grandparent, etc.).
 * 
 * EXAMPLE:
 * "advent-gear-jackets": {
 *   "CategoryID": "advent-gear-jackets",
 *   "Name": "Jackets",
 *   "ParentCategoryID": "advent-health-gear",
 *   "allChildrenIds": [],
 *   "allParentIds": ["advent-health-gear", "shop-by-position-department"]
 * }
 * 
 * USAGE:
 * This enriched data structure allows for efficient lookups to determine category relationships,
 * such as checking if a given category is a descendant of another, which is used for rendering
 * dynamic content assets on Product Listing Pages (PLPs) based on parent category conditions.
 */
const initialState = {
    categoryHierarchyData: {}, // Stores enriched category data with parent/child relationships
    siteId: '',
    companyNumber: 0,
    timeout: null,
    loading: false,
    websiteLogoInLocalStorage: "",
    urlStack: [],
    langCode: 'en',
    siteName: "Basic B2B",
    isChatEnabled: false,
    isSubmitOrderEnabled: true,
    selfRegister: false,
    loginSystem: 1,
    minicartDisabled: false,
    homeBanner: {
        isLoading: false,
        slides: []
    },
    localeData: {
        'es': [],
    },
    siteLocations: [],
    siteDefaultLocation: null,
    isPBEEnabled: false,
    isQualtricsEnabled: false,
    displayPBERecipientListOnPDP: false,
    shipToLocationEnabledForUser: false,
    shipToLocationEnabled: false,
    isPayrollDeductionEnabled: false,
    enableGiftCardWithEmployeeCredits: false,
    smartyStreets: {
        acSettings: {},
        avsSettings: {},
    },
    billingAddressEditable: false,
    shippingAddressEditable: false,
    retokenizing: false,
    sessionExpired: false,
    sessionExpiringSoon: false,
    currentCategoryId: null,
    serviceWorkerInitialized: false,
    serviceWorkerUpdated: false,
    serviceWorkerRegistration: null,
    allotmentCompleteContinueShopping: '',
    embChoiceDisplayDefault: 1,
    embChoiceDisplayGreaterThan: 3,
    embChoiceDisplayGreaterThanOverride: 2,
    embChoiceDropdownDefaultOptionText: '',
    minSizeOptionsForDropdown: 20,
    trackingLink: '',
    additionalSettings: {},
    enableLogRocket: false,
    logRocketAppID: '',
    lrClientIpHash: '',
    sizingPortalEnabled: false,
    enableSSO: false,
    samlLogoutEndpoint: '',
    samlLoginEndpoint: '',
    orderConfirmationMessage: '',
    allotmentData: {},
    isPersonalInfoModificationAllowed: true,
    isDedicatedAllotmentAddressesEnabled: false,
    isAddAllotmentAddressEnabled: false,
    isPurchaseOrderNumberRequired: false,
    enablePayPalForOrderAmountGreaterThen: 0,
    enablePayPalPayments: false,
    isPayPalRestrictBasketTotal: false,
    paypalRestrictBasketTotalValue: 0,
    ppApiApplePayEnabled: false,
    ppApiApplePaySdkUrl: '',
    cachedContentAssets: {},
    dynamicContentAssets: null,
    storefrontStatus: ''
}

/*
Builds an enriched category hierarchy object from a flat array of categories.
Each key is a CategoryID, and the value contains its details along with
arrays of all its children IDs and all its parent IDs.
*/
const buildCategoryHierarchy = (rawCategoryArray) => {
    if (!Array.isArray(rawCategoryArray)) {
        console.error('buildCategoryHierarchy expected an array, but received:', rawCategoryArray);
        return {};
    }

    const categoryMap = rawCategoryArray.reduce((map, cat) => {
        map[cat.CategoryID] = cat;
        return map;
    }, {});

    const childrenMap = rawCategoryArray.reduce((map, cat) => {
        if (cat.ParentCategoryID) {
            if (!map[cat.ParentCategoryID]) map[cat.ParentCategoryID] = [];
            map[cat.ParentCategoryID].push(cat.CategoryID);
        }
        return map;
    }, {});

    const findAllChildrenRecursive = (categoryId, allChildren = new Set()) => {
        const children = childrenMap[categoryId];
        if (children) {
            children.forEach(childId => {
                if (!allChildren.has(childId)) {
                    allChildren.add(childId);
                    findAllChildrenRecursive(childId, allChildren);
                }
            });
        }
        return allChildren;
    };

    const findAllParentsRecursive = (categoryId, allParents = new Set()) => {
        const category = categoryMap[categoryId];
        if (category && category.ParentCategoryID) {
            const parentId = category.ParentCategoryID;
            if (categoryMap[parentId] && !allParents.has(parentId)) {
                allParents.add(parentId);
                findAllParentsRecursive(parentId, allParents);
            }
        }
        return allParents;
    };

    const enrichedCategories = {};
    for (const categoryId in categoryMap) {
        if (Object.hasOwnProperty.call(categoryMap, categoryId)) {
            enrichedCategories[categoryId] = {
                ...categoryMap[categoryId], // Spread the original category object
                allChildrenIds: Array.from(findAllChildrenRecursive(categoryId)),
                allParentIds: Array.from(findAllParentsRecursive(categoryId)),
            };
        }
    }
    return enrichedCategories;
};

export const commonSlice = createSlice({
    name: 'common',
    initialState,
    reducers: {
        setAllotmentData: (state, action) => {
            state.allotmentData = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setStorefrontStatus: (state, action) => {
            state.storefrontStatus = action.payload;
        },
        setSiteId: (state, action) => {
            state.siteId = action.payload;
        },
        setWebsiteLogo: (state, action) => {
            state.websiteLogoInLocalStorage = action.payload;
        },
        setLangCodeInRedux: (state, action) => {
            state.langCode = action.payload;
        },
        setUserShipToLocationEnabled: (state, action) => {
            state.shipToLocationEnabledForUser = action.payload;
        },
        disableMiniCart: (state, action) => {
            state.minicartDisabled = action.payload;
        },
        isRetokenizing: (state, action) => {
            state.retokenizing = action.payload;
        },
        sessionExpiringSoon: (state, action) => {
            state.sessionExpiringSoon = action.payload;
        },
        sessionExpired: (state, action) => {
            state.sessionExpired = action.payload;
        },
        setCurrentCategoryId: (state, action) => {
            state.currentCategoryId = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(stopLoading.pending, (state) => {
                state.loading = true;
            })
            .addCase(stopLoading.fulfilled, (state) => {
                state.loading = false;
            })
            .addMatcher(siteApi.endpoints.getSiteLogo.matchFulfilled, (state, action) => {
                state.websiteLogoInLocalStorage = action.payload.url;
            })
            .addMatcher(siteApi.endpoints.getSiteLogo.matchFulfilled, (state, action) => {
                state.websiteLogoInLocalStorage = action.payload.url;
            })
            .addMatcher(siteApi.endpoints.getContentAssetCache.matchFulfilled, (state, action) => {
                state.cachedContentAssets[action.payload.ContentKey] = action.payload.ContentValue;
            })
            .addMatcher(siteApi.endpoints.getSiteInfo.matchFulfilled, (state, action) => {
                state.siteId = action.payload.id;
                state.companyNumber = action.payload.companyNumber ?? 0;
                state.siteName = action.payload.name;
                state.isChatEnabled = action.payload.isChatEnabled;
                state.isSubmitOrderEnabled = action.payload.isSubmitOrderEnabled;
                state.selfRegister = action.payload.selfRegister;
                state.loginSystem = action.payload.loginSystem;
                state.isPBEEnabled = action.payload.pbeEnabled;
                state.isQualtricsEnabled = action.payload.qualtricsEnabled;
                state.sizingPortalEnabled = action.payload.sizingPortalEnabled;
                state.displayPBERecipientListOnPDP = action.payload.displayPBERecipientListOnPDP;
                state.shipToLocationEnabled = action.payload.shipToLocationEnabled;
                state.siteLocations = action.payload.allLocations;
                state.siteDefaultLocation = action.payload.defaultLocation;
                state.isPayrollDeductionEnabled = action.payload.isPayrollDeductionEnabled;
                state.enableGiftCardWithEmployeeCredits = action.payload.enableGiftCardWithEmployeeCredits ?? false;
                state.smartyStreets = {
                    acSettings: action.payload.smartyACSettings,
                    avsSettings: action.payload.smartyAVSSettings,
                };
                state.billingAddressEditable = action.payload.billingAddressEditable;
                state.shippingAddressEditable = action.payload.shippingAddressEditable;
                state.allotmentCompleteContinueShopping = action.payload.allotmentCompleteContinueShopping;
                state.embChoiceDisplayDefault = action.payload.embChoiceDisplayDefault;
                state.embChoiceDisplayGreaterThan = action.payload.embChoiceDisplayGreaterThan;
                state.embChoiceDisplayGreaterThanOverride = action.payload.embChoiceDisplayGreaterThanOverride;
                state.embChoiceDropdownDefaultOptionText = action.payload.embChoiceDropdownDefaultOptionText;
                state.categories = action.payload.categories;
                state.trackingLink = action.payload.trackingLink;
                state.additionalSettings = action.payload.additionalSettings;
                state.enableLogRocket = action.payload.enableLogRocket ?? false;
                state.logRocketAppID = action.payload.logRocketAppID ?? '';
                state.lrClientIpHash = action.payload.lrClientIpHash ?? '';
                state.enableSSO = action.payload.enableSSO;
                state.samlLogoutEndpoint = action.payload.samlLogoutEndpoint;
                state.samlLoginEndpoint = action.payload.samlLoginEndpoint;
                state.orderConfirmationMessage = action.payload.orderConfirmationMessage;
                state.isShippingTaxExempt = action.payload.isShippingTaxExempt;
                state.isTaxExemptAppliedToMaxItem = action.payload.isTaxExemptAppliedToMaxItem;
                state.listIgnoreTaxExempt = action.payload.listIgnoreTaxExempt;
                state.shipToLocationAddressFallback = action.payload.shipToLocationAddressFallback;
                state.payrollDeductionSelectOptions = action.payload.payrollDeductionSelectOptions;
                state.enableSSO = action.payload.enableSSO;
                state.samlLogoutEndpoint = action.payload.samlLogoutEndpoint;
                state.samlLoginEndpoint = action.payload.samlLoginEndpoint;
                state.isPersonalInfoModificationAllowed = action.payload.isPersonalInfoModificationAllowed;
                state.isDedicatedAllotmentAddressesEnabled = action.payload.isDedicatedAllotmentAddressesEnabled;
                state.isAddAllotmentAddressEnabled = action.payload.isAddAllotmentAddressDuringCheckoutEnabled;
                state.isPurchaseOrderNumberRequired = action.payload.isPurchaseOrderNumberRequired;
                state.enablePayPalForOrderAmountGreaterThen = action.payload.enablePayPalForOrderAmountGreaterThen;
                state.enablePayPalPayments = action.payload.enablePayPalPayments;
                state.isPayPalRestrictBasketTotal = action.payload.isPayPalRestrictBasketTotal;
                state.paypalRestrictBasketTotalValue = action.payload.paypalRestrictBasketTotalValue;
                state.ppApiApplePayEnabled = action.payload.ppApiApplePayEnabled;
                state.ppApiApplePaySdkUrl = action.payload.ppApiApplePaySdkUrl;
                for (let i = 0; i < action.payload.contentAssets?.StaticContentAssets?.length; i++) {
                    state.cachedContentAssets[action.payload.contentAssets.StaticContentAssets[i].Region] = action.payload.contentAssets.StaticContentAssets[i].Content;
                }
                state.dynamicContentAssets = action.payload.contentAssets?.DynamicContentAssets;
            })
            .addMatcher(siteApi.endpoints.getCategories.matchFulfilled, (state, action) => {
                const categoryArray = action.payload?.respObject || [];
                state.categoryHierarchyData = buildCategoryHierarchy(categoryArray);
            })
            .addMatcher(contentApi.endpoints.getHomeBanner.matchFulfilled, (state) => {
                state.homeBanner.isLoading = true;
            })
            .addMatcher(contentApi.endpoints.getHomeBanner.matchRejected, (state) => {
                state.homeBanner.isLoading = false;
            })
            .addMatcher(contentApi.endpoints.getHomeBanner.matchFulfilled, (state, action) => {
                state.homeBanner.isLoading = false;
                state.homeBanner.slides = action.payload;
            })
            .addMatcher(siteApi.endpoints.getLocaleData.matchFulfilled, (state, action) => {
                state.localeData = {
                    ...action.payload,
                }
            })
    }
})

export const { setUser, logOutUser } = commonSlice.actions;
export const commonActions = commonSlice.actions;
export default commonSlice.reducer;